import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { sort } from 'fast-sort'
import { firstBy } from 'thenby'
import Select, { components } from 'react-select'
import colors from '../../../configuration/colors'
import reactSelectBaseStyles from '../../../configuration/ReactSelect'
import FlexAlignCenter from '../../styled/FlexAlignCenter'
import EvidenceStatements from '../../mini/Skills/EvidenceStatements'
import DraftJSRenderer from '../../mini/DraftJSRenderer'
import useLexicon from '../../../hooks/useLexicon'

const skillsStyles = Object.assign({}, reactSelectBaseStyles)

skillsStyles.singleValue = provided => {
  return {
    ...provided,
    backgroundColor: colors.white,
    width: '100%',
    margin: '0',
    cursor: 'pointer',
    color: colors.black,
    fontSize: '14px',
    fontWeight: '400'
  }
}

skillsStyles.container = provided => {
  return {
    ...provided,
    width: '100%',
    height: '44px',
    zIndex: '20'
  }
}

skillsStyles.control = (provided, state) => {
  return {
    ...provided,
    width: '100%',
    height: '44px',
    padding: '2px 0',
    boxShadow: 'none',
    borderColor: `${colors.rock} !important`,
    background: colors.white,
    zIndex: '20'
  }
}

skillsStyles.input = provided => {
  return {
    ...provided,
    color: colors.black,
    fontSize: '14px',
    fontWeight: '400'
  }
}

skillsStyles.option = (provided, state) => {
  return {
    ...provided,
    background: state.isFocused ? '#f1f1f1' : colors.white,
    color: colors.black,
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '400',
    cursor: 'pointer'
  }
}

const SingleValue = innerProps => {
  const { label, focusSkill, activityIndex } = innerProps.data
  const lexicon = useLexicon()
  return (
    <components.SingleValue {...innerProps}>
      <FlexAlignCenter
        style={{
          justifyContent: 'space-between',
          width: '100%',
          paddingLeft: '12px',
          fontSize: '14px',
          fontWeight: '300',
          cursor: 'pointer'
        }}
        {...innerProps}
      >
        <div>
          {focusSkill && (
            <span style={{ fontWeight: '700', marginRight: '6px' }}>
              Focus {lexicon.skill}:
            </span>
          )}
          <span>{label}</span>
        </div>
        {focusSkill && (
          <div style={{ marginRight: '60px' }}>{lexicon.activity} {activityIndex}</div>
        )}
      </FlexAlignCenter>
    </components.SingleValue>
  )
}

const Option = innerProps => {
  const { label, focusSkill, activityIndex } = innerProps.data
  const lexicon = useLexicon()
  return (
    <components.Option {...innerProps}>
      <FlexAlignCenter
        style={{
          justifyContent: 'space-between',
          width: '100%',
          paddingLeft: '12px',
          fontSize: '14px',
          fontWeight: '300',
          background: innerProps.isFocused
            ? colors.selectOptionFocusBackground
            : colors.white,
          cursor: 'pointer'
        }}
        {...innerProps}
      >
        <div>
          {focusSkill && (
            <span style={{ fontWeight: '700', marginRight: '6px' }}>
              Focus {lexicon.skill}:
            </span>
          )}
          <span>{label}</span>
        </div>
        {focusSkill && (
          <div style={{ marginRight: '80px' }}>{lexicon.activity} {activityIndex}</div>
        )}
      </FlexAlignCenter>
    </components.Option>
  )
}

const getSkillOptionObject = (
  skill,
  focusSkills,
  userQuestActivities,
  focusSkill
) => {
  const uqa = focusSkill
    ? userQuestActivities.find(
        uqa => uqa.activity_id === focusSkill.activity_id
      )
    : null
  return {
    value: skill.id,
    label: skill.name,
    evidenceStatementSetId: skill.evidence_statement_set_id,
    evidenceStatements: skill.evidence_statements.sort(firstBy('sort_order')),
    focusSkill,
    activityIndex: uqa ? uqa.number : null,
    userQuestActivityId: uqa ? uqa.id : null
  }
}

const SkillSelectMentor = ({
  index,
  skillCheck,
  quest,
  frameworkSkills,
  focusSkills,
  selectedSkills,
  onSkillSelect
}) => {
  const getSelectedSkill = () => {
    const skill = frameworkSkills.find(s => s.id === skillCheck.skillId) || null
    return skill
      ? getSkillOptionObject(
          skill,
          focusSkills,
          quest.user_quest_activities,
          skillCheck.focusSkill
        )
      : null
  }

  const getFocusSkillOptions = () => {
    // filter out activity focus skills that are selected in other skill cards
    const uqaIds = quest.user_quest_activities.map(uqa => uqa.activity_id)
    return focusSkills
      .filter(fs => uqaIds.includes(fs.activity_id))
      .filter(
        fs =>
          !selectedSkills
            .filter(
              ss =>
                ss.focusSkill &&
                ss.skillId === fs.framework_skill_id &&
                ss.focusSkill.activity_id === fs.activity_id
            )
            .map(ss => ss.skillId)
            .includes(fs.framework_skill_id)
      )
      .sort(firstBy('name'))
      .map(fs =>
        getSkillOptionObject(
          frameworkSkills.find(frss => frss.id === fs.framework_skill_id),
          focusSkills,
          quest.user_quest_activities,
          fs
        )
      )
  }

  const getSkillOptions = () =>
    // filter out skills that are selected in other skill cards and are not a focus skill
    frameworkSkills
      .filter(
        s =>
          !selectedSkills
            .filter(s => !s.focusSkill)
            .map(ss => ss.skillId)
            .includes(s.id)
      )
      .sort(firstBy('name'))
      .map(fs =>
        getSkillOptionObject(fs, focusSkills, quest.user_quest_activities, null)
      )

  const selectedSkill = getSelectedSkill(frameworkSkills, skillCheck.skillId)
  const skillOptions = sort([
    ...getFocusSkillOptions(),
    ...getSkillOptions()
  ]).asc([s => !s.focusSkill, s => s.label])

  return (
    <Fragment>
      <FlexAlignCenter
        style={{ justifyContent: 'unset', marginBottom: '15px' }}
      >
        <Select
          autoFocus
          isClearable={false}
          isSearchable
          options={skillOptions}
          value={selectedSkill}
          onChange={selectedOption => {
            onSkillSelect(selectedOption, index)
          }}
          placeholder="Select or type in a skill"
          noOptionsMessage={() =>
            'There are no records that match your search.'
          }
          styles={skillsStyles}
          components={{
            SingleValue: SingleValue,
            Option: Option
          }}
        />
      </FlexAlignCenter>
      {selectedSkill && (
        <div style={{ textAlign: 'left' }}>
          {!selectedSkill.focusSkill ||
          (selectedSkill.focusSkill &&
            (!selectedSkill.focusSkill.student_guidance ||
              selectedSkill.focusSkill.student_guidance.trim().length ===
                0)) ? (
            <EvidenceStatements
              evidenceStatements={selectedSkill.evidenceStatements}
              isTooltip={false}
            />
          ) : (
            <DraftJSRenderer
              content={selectedSkill.focusSkill.student_guidance}
            />
          )}
        </div>
      )}
    </Fragment>
  )
}

SkillSelectMentor.propTypes = {
  dispatch: PropTypes.func,
  index: PropTypes.number,
  skillCheck: PropTypes.object,
  fetchSkills: PropTypes.func,
  onSkillSelect: PropTypes.func,
  selectedSkills: PropTypes.array,
  quest: PropTypes.object,
  frameworkSkills: PropTypes.array,
  focusSkills: PropTypes.array
}

export default SkillSelectMentor
